import React, { createContext, useContext, ReactNode } from 'react';

const asBool = (v: string | undefined, def: boolean) => {
  if (v === undefined || v === null || v === '' || !v) {
    return def;
  }
  
  const val = v.trim().toLowerCase();
  
  if (val === 'true' || val === '1') return true;
  if (val === 'false' || val === '0') return false;
  
  return def;
};

export type TEMPLATE_TYPE = 'phaser-react' | 'phaser' | 'single';

// export type PAYMENT_APPROACH = 'free' | 'free-trial' | 'paid-trial' | 'hard-paywall';

interface ConfigContextType {
  USE_CODE_FROM_STORAGE: boolean;
  DEBUG_EXPORT: boolean;
  DEBUG_GENERATE: boolean;
  DEBUG_DISABLE_IMAGE_AI: boolean;
  DEBUG_DISABLE_3D_AI: boolean;
  DEBUG_DISABLE_ANIMATION_AI: boolean;
  DEBUG_DISABLE_SOUND_AI: boolean;
  DEBUG_DISABLE_CODE_AI: boolean;

  AUTO_GENERATE_IMAGE_DELAY: number;
  AUTO_GENERATE_IMAGE_TIMEOUT: number;
  AUTO_GENERATE_IMAGE_MAX_RETRIES: number;
  IMAGE_SIZE: number;
  SHOW_DRAW_HISTORY_DESCRIPTION: boolean;
  SHOW_DRAW_HISTORY_USER_IMAGE: boolean;
  SHOW_ADDITIONAL_ART_TOOLS: boolean;
  SHOW_NEW_HISTORY: boolean;
  ART_REALTIME: boolean;
  ENABLE_GAME_ART_MENU: boolean;
  AUTO_OPEN_ASSETS_PANEL_ON_ASSET_CHANGE: boolean;

  GENERATE_3D_TIMEOUT: number;

  SOUND_AI_MODEL: string;
  SOUND_AI_TIMEOUT: number;

  CODE_FILES_MODE: 'single' | 'multiple';
  GAME_BALANCE_PANEL: boolean;
  AUTO_OPEN_BALANCE_PANEL_ON_CHANGE: boolean;
  GAME_ASSETS_INTEGRATION: { panel: boolean; input: boolean; debug_init_button: boolean; sound: boolean; auto_trigger_init: boolean; };
  DEBUG_CODE_MODEL_TOGGLE: boolean;
  DEBUG_CODE_TEMPLATE_BUTTON: boolean;
  DEBUG_SHOW_IN_PROGRESS_HISTORY_ENTRIES: boolean;
  DEBUG_GAME_TABS: boolean;

  DEBUG_PLAN: boolean;
  GAME_DETAILED_MESSAGES: boolean;
  DEBUG_STREAM: boolean;
  DEBUG_STREAM_REAL_TIMING: boolean;
  DEBUG_STREAM_TOOLS: boolean;
  SHOW_TOOL_SPECIFIC_ICONS: boolean;
  ENABLE_AI_STREAM: boolean;
  NEW_STREAM_URL: boolean;
  GAME_ART_NEW_STREAM_URL: boolean;
  STREAM_TEST_PAGE: boolean;
  CAPTURE_STREAM_DATA: boolean;
  CONSOLE_LOG_STREAM: 'all' | 'basic' | null;
  STREAM_AUTO_SCROLL: boolean;
  ENABLE_STREAM_CANCEL: boolean;
  STREAM_HIDE_INCOMPLETE_MARKDOWN: boolean;
  STREAM_PARALLEL_SUBAGENTS: boolean;
  SHOW_SUB_AGENT_CONTENT: boolean;
  STREAM_SUB_AGENT_COLORS: boolean;
  USE_CLOUDFLARE_GAME_STREAM: boolean;

  ENABLE_ANIMATION: boolean;
  ANIMATION_SHOW_NAV_OPTION: boolean;
  ENABLE_ANIMATE_CREATE_IMAGE: boolean;
  VIDEO_TRANSFORM_APPROACH: 'ffmpeg' | 'cloudflare';
  USE_SUPABASE_ANIMATE: boolean;
  ENABLE_ANIMATE_BUTTON_IN_ART: boolean;
  ENABLE_SOUND: boolean;
  DISPLAY_GAME_METRICS: boolean;
  DEBUG_GAME_METRICS: boolean;
  CURSOR_EFFECT: boolean;
  GITHUB_SYNC: boolean;
  ENABLE_STRIPE_REDIRECTS: boolean;
  USE_NEW_GAME_ART_HISTORY: boolean;
  ENABLE_GAME_MORE_MENU: boolean;
  ENABLE_TRIAL: boolean;
  TRIAL_IS_PAID: boolean;
  DISABLE_FREE_USERS_DAILY_CREDITS: boolean;
  HIDE_FREE_VERSION: boolean;

  REDIRECT_TO_WEBSITE: boolean;
  ADMIN_EMAILS: string[];
}

const config: ConfigContextType = {
  USE_CODE_FROM_STORAGE: asBool(process.env.REACT_APP_USE_CODE_FROM_STORAGE, false),
  DEBUG_EXPORT: false,
  DEBUG_GENERATE: false,
  DEBUG_DISABLE_IMAGE_AI: false,
  DEBUG_DISABLE_3D_AI: false,
  DEBUG_DISABLE_ANIMATION_AI: false,
  DEBUG_DISABLE_SOUND_AI: false,
  DEBUG_DISABLE_CODE_AI: false,

  AUTO_GENERATE_IMAGE_DELAY: 1000,
  AUTO_GENERATE_IMAGE_TIMEOUT: 60000, // 60 seconds
  AUTO_GENERATE_IMAGE_MAX_RETRIES: 3,
  IMAGE_SIZE: 512,
  SHOW_DRAW_HISTORY_DESCRIPTION: false,
  SHOW_DRAW_HISTORY_USER_IMAGE: true,
  SHOW_ADDITIONAL_ART_TOOLS: true,
  SHOW_NEW_HISTORY: true,
  ART_REALTIME: false,
  ENABLE_GAME_ART_MENU: false,
  AUTO_OPEN_ASSETS_PANEL_ON_ASSET_CHANGE: asBool(process.env.REACT_APP_AUTO_OPEN_ASSETS_PANEL_ON_ASSET_CHANGE, false),

  GENERATE_3D_TIMEOUT: 3 * 60 * 1000, // 3 minutes

  SOUND_AI_MODEL: "fal-ai/elevenlabs/sound-effects",
  SOUND_AI_TIMEOUT: 30000, // 30 seconds for sound generation

  CODE_FILES_MODE: 'multiple',
  GAME_BALANCE_PANEL: true,
  AUTO_OPEN_BALANCE_PANEL_ON_CHANGE: asBool(process.env.REACT_APP_AUTO_OPEN_BALANCE_PANEL_ON_CHANGE, false),
  GAME_ASSETS_INTEGRATION: {
    panel: true,
    input: false,
    sound: false,
    debug_init_button: asBool(process.env.REACT_APP_GAME_ASSETS_DEBUG_INIT_BUTTON, false),
    auto_trigger_init: asBool(process.env.REACT_APP_GAME_ASSETS_AUTO_TRIGGER_INIT, true),
  },
  DEBUG_CODE_MODEL_TOGGLE: false,
  DEBUG_CODE_TEMPLATE_BUTTON: false,
  DEBUG_SHOW_IN_PROGRESS_HISTORY_ENTRIES: false,
  DEBUG_GAME_TABS: asBool(process.env.REACT_APP_DEBUG_GAME_TABS, true),

  DEBUG_PLAN: false,
  GAME_DETAILED_MESSAGES: false,
  DEBUG_STREAM: false,
  DEBUG_STREAM_REAL_TIMING: true,
  ENABLE_AI_STREAM: true,
  NEW_STREAM_URL: true,
  GAME_ART_NEW_STREAM_URL: true,
  DEBUG_STREAM_TOOLS: asBool(process.env.REACT_APP_DEBUG_STREAM_TOOLS, false),
  SHOW_TOOL_SPECIFIC_ICONS: false,
  STREAM_TEST_PAGE: false,
  CAPTURE_STREAM_DATA: false,
  CONSOLE_LOG_STREAM: (() => {
    const val = process.env.REACT_APP_CONSOLE_LOG_STREAM;
    if (val === 'all' || val === 'basic') return val;
    return null;
  })(),
  STREAM_AUTO_SCROLL: asBool(process.env.REACT_APP_STREAM_AUTO_SCROLL, true),
  ENABLE_STREAM_CANCEL: false,
  STREAM_HIDE_INCOMPLETE_MARKDOWN: false,
  STREAM_SUB_AGENT_COLORS: true,
  STREAM_PARALLEL_SUBAGENTS: true,
  SHOW_SUB_AGENT_CONTENT: asBool(process.env.REACT_APP_SHOW_SUB_AGENT_CONTENT, false),
  USE_CLOUDFLARE_GAME_STREAM: asBool(process.env.REACT_APP_USE_CLOUDFLARE_GAME_STREAM, false),

  ENABLE_ANIMATION: asBool(process.env.REACT_APP_ENABLE_ANIMATION, false),
  ANIMATION_SHOW_NAV_OPTION: asBool(process.env.REACT_APP_ANIMATION_SHOW_NAV_OPTION, false),
  ENABLE_ANIMATE_CREATE_IMAGE: true,
  VIDEO_TRANSFORM_APPROACH: 'cloudflare',
  USE_SUPABASE_ANIMATE: true,
  ENABLE_ANIMATE_BUTTON_IN_ART: false,
  ENABLE_SOUND: false,
  DISPLAY_GAME_METRICS: false,
  DEBUG_GAME_METRICS: false,
  CURSOR_EFFECT: true,
  GITHUB_SYNC: asBool(process.env.REACT_APP_GITHUB_SYNC, false),
  ENABLE_STRIPE_REDIRECTS: false,
  USE_NEW_GAME_ART_HISTORY: true,
  ENABLE_GAME_MORE_MENU: asBool(process.env.REACT_APP_ENABLE_GAME_MORE_MENU, false),
  ENABLE_TRIAL: asBool(process.env.REACT_APP_ENABLE_TRIAL, false),
  TRIAL_IS_PAID: asBool(process.env.REACT_APP_TRIAL_IS_PAID, false),
  DISABLE_FREE_USERS_DAILY_CREDITS: asBool(process.env.REACT_APP_DISABLE_FREE_USERS_DAILY_CREDITS, false),
  HIDE_FREE_VERSION: true,

  REDIRECT_TO_WEBSITE: true,
  ADMIN_EMAILS: ['eve.silberman7390@gmail.com'],
};

const ConfigContext = createContext<ConfigContextType>(config);

export const ConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <ConfigContext.Provider value={config}>
      {children}
    </ConfigContext.Provider>
  );
};

export function useConfig() {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}

// Static config values that don't need context
export const defaultConfig = {
  USE_CODE_FROM_STORAGE: config.USE_CODE_FROM_STORAGE,
  DEBUG_EXPORT: config.DEBUG_EXPORT,
  DEBUG_GENERATE: config.DEBUG_GENERATE,
  DEBUG_DISABLE_IMAGE_AI: config.DEBUG_DISABLE_IMAGE_AI,
  DEBUG_DISABLE_3D_AI: config.DEBUG_DISABLE_3D_AI,
  DEBUG_DISABLE_ANIMATION_AI: config.DEBUG_DISABLE_ANIMATION_AI,
  DEBUG_DISABLE_SOUND_AI: config.DEBUG_DISABLE_SOUND_AI,
  DEBUG_DISABLE_CODE_AI: config.DEBUG_DISABLE_CODE_AI,

  AUTO_GENERATE_IMAGE_DELAY: config.AUTO_GENERATE_IMAGE_DELAY,
  AUTO_GENERATE_IMAGE_TIMEOUT: config.AUTO_GENERATE_IMAGE_TIMEOUT,
  AUTO_GENERATE_IMAGE_MAX_RETRIES: config.AUTO_GENERATE_IMAGE_MAX_RETRIES,
  IMAGE_SIZE: config.IMAGE_SIZE,
  SHOW_DRAW_HISTORY_DESCRIPTION: config.SHOW_DRAW_HISTORY_DESCRIPTION,
  SHOW_DRAW_HISTORY_USER_IMAGE: config.SHOW_DRAW_HISTORY_USER_IMAGE,
  SHOW_ADDITIONAL_ART_TOOLS: config.SHOW_ADDITIONAL_ART_TOOLS,
  SHOW_NEW_HISTORY: config.SHOW_NEW_HISTORY,
  ART_REALTIME: config.ART_REALTIME,
  ENABLE_GAME_ART_MENU: config.ENABLE_GAME_ART_MENU,
  AUTO_OPEN_ASSETS_PANEL_ON_ASSET_CHANGE: config.AUTO_OPEN_ASSETS_PANEL_ON_ASSET_CHANGE,

  GENERATE_3D_TIMEOUT: config.GENERATE_3D_TIMEOUT,

  SOUND_AI_MODEL: config.SOUND_AI_MODEL,
  SOUND_AI_TIMEOUT: config.SOUND_AI_TIMEOUT,

  CODE_FILES_MODE: config.CODE_FILES_MODE,
  GAME_BALANCE_PANEL: config.GAME_BALANCE_PANEL,
  AUTO_OPEN_BALANCE_PANEL_ON_CHANGE: config.AUTO_OPEN_BALANCE_PANEL_ON_CHANGE,
  GAME_ASSETS_INTEGRATION: config.GAME_ASSETS_INTEGRATION,
  DEBUG_CODE_MODEL_TOGGLE: config.DEBUG_CODE_MODEL_TOGGLE,
  DEBUG_CODE_TEMPLATE_BUTTON: config.DEBUG_CODE_TEMPLATE_BUTTON,
  DEBUG_SHOW_IN_PROGRESS_HISTORY_ENTRIES: config.DEBUG_SHOW_IN_PROGRESS_HISTORY_ENTRIES,
  DEBUG_GAME_TABS: config.DEBUG_GAME_TABS,

  DEBUG_PLAN: config.DEBUG_PLAN,
  GAME_DETAILED_MESSAGES: config.GAME_DETAILED_MESSAGES,
  DEBUG_STREAM: config.DEBUG_STREAM,
  DEBUG_STREAM_REAL_TIMING: config.DEBUG_STREAM_REAL_TIMING,
  DEBUG_STREAM_TOOLS: config.DEBUG_STREAM_TOOLS,
  SHOW_TOOL_SPECIFIC_ICONS: config.SHOW_TOOL_SPECIFIC_ICONS,
  ENABLE_AI_STREAM: config.ENABLE_AI_STREAM,
  NEW_STREAM_URL: config.NEW_STREAM_URL,
  GAME_ART_NEW_STREAM_URL: config.GAME_ART_NEW_STREAM_URL,
  STREAM_TEST_PAGE: config.STREAM_TEST_PAGE,
  CAPTURE_STREAM_DATA: config.CAPTURE_STREAM_DATA,
  CONSOLE_LOG_STREAM: config.CONSOLE_LOG_STREAM,
  STREAM_AUTO_SCROLL: config.STREAM_AUTO_SCROLL,
  ENABLE_STREAM_CANCEL: config.ENABLE_STREAM_CANCEL,
  STREAM_HIDE_INCOMPLETE_MARKDOWN: config.STREAM_HIDE_INCOMPLETE_MARKDOWN,
  STREAM_PARALLEL_SUBAGENTS: config.STREAM_PARALLEL_SUBAGENTS,
  SHOW_SUB_AGENT_CONTENT: config.SHOW_SUB_AGENT_CONTENT,
  STREAM_SUB_AGENT_COLORS: config.STREAM_SUB_AGENT_COLORS,
  USE_CLOUDFLARE_GAME_STREAM: config.USE_CLOUDFLARE_GAME_STREAM,

  ENABLE_ANIMATION: config.ENABLE_ANIMATION,
  ANIMATION_SHOW_NAV_OPTION: config.ANIMATION_SHOW_NAV_OPTION,
  ENABLE_ANIMATE_CREATE_IMAGE: config.ENABLE_ANIMATE_CREATE_IMAGE,
  VIDEO_TRANSFORM_APPROACH: config.VIDEO_TRANSFORM_APPROACH,
  USE_SUPABASE_ANIMATE: config.USE_SUPABASE_ANIMATE,
  ENABLE_ANIMATE_BUTTON_IN_ART: config.ENABLE_ANIMATE_BUTTON_IN_ART,
  ENABLE_SOUND: config.ENABLE_SOUND,
  DISPLAY_GAME_METRICS: config.DISPLAY_GAME_METRICS,
  DEBUG_GAME_METRICS: config.DEBUG_GAME_METRICS,
  CURSOR_EFFECT: config.CURSOR_EFFECT,
  GITHUB_SYNC: config.GITHUB_SYNC,
  ENABLE_STRIPE_REDIRECTS: config.ENABLE_STRIPE_REDIRECTS,
  USE_NEW_GAME_ART_HISTORY: config.USE_NEW_GAME_ART_HISTORY,
  ENABLE_GAME_MORE_MENU: config.ENABLE_GAME_MORE_MENU,
  ENABLE_TRIAL: config.ENABLE_TRIAL,
  TRIAL_IS_PAID: config.TRIAL_IS_PAID,
  DISABLE_FREE_USERS_DAILY_CREDITS: config.DISABLE_FREE_USERS_DAILY_CREDITS,
  HIDE_FREE_VERSION: config.HIDE_FREE_VERSION,

  REDIRECT_TO_WEBSITE: config.REDIRECT_TO_WEBSITE,
  ADMIN_EMAILS: config.ADMIN_EMAILS,
};
